import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Clock, AlertTriangle, X, ChevronDown } from 'lucide-react';
// Question type
interface Question {
  id: number;
  text: string;
  image?: string;
  options: {
    id: string;
    text: string;
  }[];
  answer?: string;
}
// Subject type
interface Subject {
  id: string;
  name: string;
  displayName: string;
  questions: Question[];
}
// Answer key definitions - updated to 4 options (A,B,C,D)
const ANSWER_KEYS = {
  turkce: ['A', 'B', 'C', 'D', 'A', 'B', 'C', 'D', 'A', 'B', 'C', 'D', 'A', 'B', 'C', 'D', 'A', 'B', 'C', 'D'],
  inkılap: ['B', 'C', 'A', 'D', 'B', 'C', 'A', 'D', 'B', 'C'],
  din: ['C', 'A', 'B', 'D', 'C', 'A', 'B', 'D', 'C', 'A'],
  ingilizce: ['D', 'B', 'A', 'C', 'D', 'B', 'A', 'C', 'D', 'B'],
  matematik: ['A', 'C', 'B', 'D', 'A', 'C', 'B', 'D', 'A', 'C', 'B', 'D', 'A', 'C', 'B', 'D', 'A', 'C', 'B', 'D'],
  fen: ['B', 'D', 'A', 'C', 'B', 'D', 'A', 'C', 'B', 'D', 'A', 'C', 'B', 'D', 'A', 'C', 'B', 'D', 'A', 'C']
};
// Interface for student answer data
interface AnswerData {
  soru_no: number;
  ders: string;
  ogrenci_cevabi: string | null;
  dogru_cevap: string;
}
// Mock data for the exam - updated to only have 4 options (A,B,C,D)
const getExamData = (examId: string, session?: string) => {
  // Default to sözel if no session specified
  const currentSession = session || 'sozel';
  // Define subjects based on session
  const subjects: Subject[] = currentSession === 'sozel' ? [{
    id: 'turkce',
    name: 'Türkçe',
    displayName: 'Türkçe',
    questions: Array.from({
      length: 20
    }, (_, i) => ({
      id: i + 1,
      text: `Türkçe soru ${i + 1}: Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam auctor, nisl eget ultricies aliquam, nunc nisl aliquet nunc, quis aliquam nisl nunc quis nisl.`,
      image: i % 3 === 0 ? `https://picsum.photos/seed/turkce${i}/800/400` : undefined,
      options: [{
        id: 'A',
        text: 'Lorem ipsum dolor sit amet'
      }, {
        id: 'B',
        text: 'Consectetur adipiscing elit'
      }, {
        id: 'C',
        text: 'Nullam auctor, nisl eget ultricies aliquam'
      }, {
        id: 'D',
        text: 'Nunc nisl aliquet nunc, quis aliquam nisl nunc quis nisl'
      }]
    }))
  }, {
    id: 'inkılap',
    name: 'T.C. İnkılap Tarihi ve Atatürkçülük',
    displayName: 'T.C. İnkılap Tarihi ve Atatürkçülük',
    questions: Array.from({
      length: 10
    }, (_, i) => ({
      id: i + 1,
      text: `İnkılap Tarihi soru ${i + 1}: Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam auctor, nisl eget ultricies aliquam, nunc nisl aliquet nunc, quis aliquam nisl nunc quis nisl.`,
      image: i % 4 === 0 ? `https://picsum.photos/seed/inkilap${i}/800/400` : undefined,
      options: [{
        id: 'A',
        text: 'Lorem ipsum dolor sit amet'
      }, {
        id: 'B',
        text: 'Consectetur adipiscing elit'
      }, {
        id: 'C',
        text: 'Nullam auctor, nisl eget ultricies aliquam'
      }, {
        id: 'D',
        text: 'Nunc nisl aliquet nunc, quis aliquam nisl nunc quis nisl'
      }]
    }))
  }, {
    id: 'din',
    name: 'Din Kültürü ve Ahlak Bilgisi',
    displayName: 'Din Kültürü ve Ahlak Bilgisi',
    questions: Array.from({
      length: 10
    }, (_, i) => ({
      id: i + 1,
      text: `Din Kültürü soru ${i + 1}: Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam auctor, nisl eget ultricies aliquam, nunc nisl aliquet nunc, quis aliquam nisl nunc quis nisl.`,
      image: i % 5 === 0 ? `https://picsum.photos/seed/din${i}/800/400` : undefined,
      options: [{
        id: 'A',
        text: 'Lorem ipsum dolor sit amet'
      }, {
        id: 'B',
        text: 'Consectetur adipiscing elit'
      }, {
        id: 'C',
        text: 'Nullam auctor, nisl eget ultricies aliquam'
      }, {
        id: 'D',
        text: 'Nunc nisl aliquet nunc, quis aliquam nisl nunc quis nisl'
      }]
    }))
  }, {
    id: 'ingilizce',
    name: 'İngilizce',
    displayName: 'İngilizce',
    questions: Array.from({
      length: 10
    }, (_, i) => ({
      id: i + 1,
      text: `İngilizce soru ${i + 1}: Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam auctor, nisl eget ultricies aliquam, nunc nisl aliquet nunc, quis aliquam nisl nunc quis nisl.`,
      image: i % 3 === 0 ? `https://picsum.photos/seed/ingilizce${i}/800/400` : undefined,
      options: [{
        id: 'A',
        text: 'Lorem ipsum dolor sit amet'
      }, {
        id: 'B',
        text: 'Consectetur adipiscing elit'
      }, {
        id: 'C',
        text: 'Nullam auctor, nisl eget ultricies aliquam'
      }, {
        id: 'D',
        text: 'Nunc nisl aliquet nunc, quis aliquam nisl nunc quis nisl'
      }]
    }))
  }] : [{
    id: 'matematik',
    name: 'Matematik',
    displayName: 'Matematik',
    questions: Array.from({
      length: 20
    }, (_, i) => ({
      id: i + 1,
      text: `Matematik soru ${i + 1}: Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam auctor, nisl eget ultricies aliquam, nunc nisl aliquet nunc, quis aliquam nisl nunc quis nisl.`,
      image: i % 3 === 0 ? `https://picsum.photos/seed/matematik${i}/800/400` : undefined,
      options: [{
        id: 'A',
        text: 'Lorem ipsum dolor sit amet'
      }, {
        id: 'B',
        text: 'Consectetur adipiscing elit'
      }, {
        id: 'C',
        text: 'Nullam auctor, nisl eget ultricies aliquam'
      }, {
        id: 'D',
        text: 'Nunc nisl aliquet nunc, quis aliquam nisl nunc quis nisl'
      }]
    }))
  }, {
    id: 'fen',
    name: 'Fen Bilimleri',
    displayName: 'Fen Bilimleri',
    questions: Array.from({
      length: 20
    }, (_, i) => ({
      id: i + 1,
      text: `Fen Bilimleri soru ${i + 1}: Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam auctor, nisl eget ultricies aliquam, nunc nisl aliquet nunc, quis aliquam nisl nunc quis nisl.`,
      image: i % 4 === 0 ? `https://picsum.photos/seed/fen${i}/800/400` : undefined,
      options: [{
        id: 'A',
        text: 'Lorem ipsum dolor sit amet'
      }, {
        id: 'B',
        text: 'Consectetur adipiscing elit'
      }, {
        id: 'C',
        text: 'Nullam auctor, nisl eget ultricies aliquam'
      }, {
        id: 'D',
        text: 'Nunc nisl aliquet nunc, quis aliquam nisl nunc quis nisl'
      }]
    }))
  }];
  // Update subject displayNames to show question counts
  subjects.forEach(subject => {
    const count = subject.questions.length;
    subject.displayName = `${subject.name} (${count} soru)`;
  });
  return {
    id: examId,
    title: `LGS Genel Deneme Sınavı - ${examId}`,
    subjects,
    session: currentSession,
    duration: currentSession === 'sozel' ? 75 : 80 // 75 minutes for sözel, 80 minutes for sayısal
  };
};
// Function to generate detailed answer data
const generateAnswerData = (subjectId: string, questionId: number, studentAnswer: string | null): AnswerData => {
  const answerKey = ANSWER_KEYS[subjectId as keyof typeof ANSWER_KEYS];
  const correctAnswer = answerKey[questionId - 1]; // Adjust for 0-based indexing in arrays
  return {
    soru_no: questionId,
    ders: subjectId,
    ogrenci_cevabi: studentAnswer,
    dogru_cevap: correctAnswer
  };
};
export function ExamInterface() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  // Get session from URL query params
  const searchParams = new URLSearchParams(location.search);
  const session = searchParams.get('session') || 'sozel';
  const examData = getExamData(id || '1', session);
  // State for the exam
  const [selectedSubject, setSelectedSubject] = useState<string>(examData.subjects[0].id);
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [lastQuestionIndices, setLastQuestionIndices] = useState<Record<string, number>>({});
  // Get previous session answers if available (for numerical session)
  const previousAnswers = location.state?.previousAnswers || {};
  const previousDetailedAnswers = location.state?.previousDetailedAnswers || [];
  // Enhanced answer tracking with detailed information
  const [answers, setAnswers] = useState<Record<string, Record<number, string | null>>>(previousAnswers);
  // Keep track of detailed answer data for results calculation
  const [detailedAnswers, setDetailedAnswers] = useState<AnswerData[]>(previousDetailedAnswers);
  const [timeLeft, setTimeLeft] = useState<number>(examData.duration * 60); // in seconds
  const [showFinishConfirm, setShowFinishConfirm] = useState<boolean>(false);
  // Get the current subject and question
  const currentSubject = examData.subjects.find(s => s.id === selectedSubject) || examData.subjects[0];
  const currentQuestion = currentSubject.questions[currentQuestionIndex];
  // Get only the questions for the current subject for the optical form
  const currentSubjectQuestions = currentSubject.questions;
  // Calculate the number of unanswered questions
  const getUnansweredCount = () => {
    let count = 0;
    examData.subjects.forEach(subject => {
      const subjectAnswers = answers[subject.id] || {};
      count += subject.questions.length - Object.keys(subjectAnswers).length;
    });
    return count;
  };
  // Timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleFinishSession();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  // Handle answer selection - updated to store detailed answer data
  const handleSelectAnswer = (questionId: number, optionId: string) => {
    // Update the simple answers state
    setAnswers(prev => ({
      ...prev,
      [selectedSubject]: {
        ...(prev[selectedSubject] || {}),
        [questionId]: optionId
      }
    }));
    // Generate detailed answer data
    const answerData = generateAnswerData(selectedSubject, questionId, optionId);
    // Update the detailed answers array
    setDetailedAnswers(prevData => {
      // Check if this question already has an answer
      const existingIndex = prevData.findIndex(item => item.ders === selectedSubject && item.soru_no === questionId);
      if (existingIndex >= 0) {
        // Update existing answer
        const newData = [...prevData];
        newData[existingIndex] = answerData;
        return newData;
      } else {
        // Add new answer
        return [...prevData, answerData];
      }
    });
    // Log for debugging
    console.log(`Answer selected: Subject=${selectedSubject}, Question=${questionId}, Option=${optionId}`);
  };
  // Handle question navigation
  const handleNextQuestion = () => {
    if (currentQuestionIndex < currentSubject.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };
  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };
  // Handle clearing an answer - updated to handle detailed answers
  const handleClearAnswer = () => {
    setAnswers(prev => {
      const newAnswers = {
        ...prev
      };
      if (newAnswers[selectedSubject]) {
        const subjectAnswers = {
          ...newAnswers[selectedSubject]
        };
        delete subjectAnswers[currentQuestion.id];
        newAnswers[selectedSubject] = subjectAnswers;
      }
      return newAnswers;
    });
    // Update detailed answers - mark as null (empty)
    setDetailedAnswers(prevData => {
      const existingIndex = prevData.findIndex(item => item.ders === selectedSubject && item.soru_no === currentQuestion.id);
      if (existingIndex >= 0) {
        // Update existing answer to null
        const newData = [...prevData];
        newData[existingIndex] = {
          ...newData[existingIndex],
          ogrenci_cevabi: null
        };
        return newData;
      } else {
        // Add new empty answer
        return [...prevData, generateAnswerData(selectedSubject, currentQuestion.id, null)];
      }
    });
    console.log(`Answer cleared: Subject=${selectedSubject}, Question=${currentQuestion.id}`);
  };
  // Go to specific question
  const handleGoToQuestion = (index: number) => {
    setCurrentQuestionIndex(index);
  };
  // Handle subject change
  const handleSubjectChange = (subjectId: string) => {
    // Remember current question index for the current subject
    setLastQuestionIndices(prev => ({
      ...prev,
      [selectedSubject]: currentQuestionIndex
    }));
    // Set the new subject
    setSelectedSubject(subjectId);
    // Restore last question index for the new subject or start from beginning
    const lastIndex = lastQuestionIndices[subjectId];
    setCurrentQuestionIndex(lastIndex !== undefined ? lastIndex : 0);
    setShowSubjectDropdown(false);
  };
  // Handle finish session - now passing detailed answer data
  const handleFinishSession = () => {
    // Ensure all questions have an entry in detailedAnswers (even if unanswered)
    const completeDetailedAnswers = [...detailedAnswers];
    // Go through all subjects and questions to ensure all have entries
    examData.subjects.forEach(subject => {
      subject.questions.forEach(question => {
        const existingEntry = detailedAnswers.find(a => a.ders === subject.id && a.soru_no === question.id);
        if (!existingEntry) {
          // Add entry for unanswered question
          completeDetailedAnswers.push(generateAnswerData(subject.id, question.id, null));
        }
      });
    });
    // Debug logging
    if (session === 'sozel') {
      console.log('Sözel cevapları:', completeDetailedAnswers.filter(a => a.ders === 'turkce' || a.ders === 'inkılap' || a.ders === 'din' || a.ders === 'ingilizce'));
    } else {
      console.log('Sayısal cevapları:', completeDetailedAnswers.filter(a => a.ders === 'matematik' || a.ders === 'fen'));
    }
    if (examData.session === 'sozel') {
      // Navigate to break screen
      navigate(`/online-denemeler/${id}/mola`, {
        state: {
          answers,
          detailedAnswers: completeDetailedAnswers
        }
      });
    } else {
      // Navigate directly to results screen for sayısal session with answers
      // For sayısal session, we combine both verbal and numerical answers
      navigate(`/online-denemeler/${id}/sonuc`, {
        state: {
          answers: {
            ...previousAnswers,
            ...answers
          },
          detailedAnswers: [...completeDetailedAnswers]
        }
      });
    }
  };
  // Get selected option for a question
  const getSelectedOption = (questionId: number) => {
    return answers[selectedSubject]?.[questionId] || null;
  };
  // Calculate answered questions count for the current subject
  const getAnsweredQuestionsCount = () => {
    const subjectAnswers = answers[selectedSubject] || {};
    return Object.keys(subjectAnswers).length;
  };
  return <div className="min-h-screen bg-[#080808] text-white py-8 px-4" data-id="element-1654">
      {/* Timer in top right */}
      <div className="fixed top-4 right-4 z-50 bg-black/80 border border-red-500/30 rounded-lg shadow-lg" data-id="element-1655">
        <div className="flex items-center gap-2 px-4 py-2" data-id="element-1656">
          <Clock className="w-5 h-5 text-red-400" data-id="element-1657" />
          <span className="text-xl font-bold text-red-400" data-id="element-1658">
            {formatTime(timeLeft)}
          </span>
        </div>
      </div>
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-6" data-id="element-1659">
        {/* Main content area (question) */}
        <div className="lg:w-2/3 space-y-6" data-id="element-1660">
          {/* Exam title centered above content */}
          <div className="text-center mb-8" data-id="element-1661">
            <h1 className="text-2xl font-bold text-purple-400" data-id="element-1662">
              {examData.title}
            </h1>
            <p className="text-gray-400 mt-1" data-id="element-1663">
              {examData.session === 'sozel' ? 'Sözel Bölüm' : 'Sayısal Bölüm'}
            </p>
          </div>
          {/* Question content */}
          <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600" data-id="element-1664">
            <div className="relative p-6 bg-black/90 rounded-xl" data-id="element-1665">
              <div className="prose prose-invert max-w-none" data-id="element-1666">
                <p className="text-lg" data-id="element-1667">{currentQuestion.text}</p>
              </div>
              {currentQuestion.image && <div className="mt-4" data-id="element-1668">
                  <img src={currentQuestion.image} alt="Soru görseli" className="rounded-lg max-w-full max-h-96 mx-auto" data-id="element-1669" />
                </div>}
            </div>
          </div>
          {/* Answer options */}
          <div className="space-y-4" data-id="element-1670">
            {currentQuestion.options.map(option => {
            const isSelected = answers[selectedSubject]?.[currentQuestion.id] === option.id;
            return <button key={option.id} onClick={() => handleSelectAnswer(currentQuestion.id, option.id)} className={`w-full p-4 rounded-lg text-left transition-colors flex items-center gap-4 
                  ${isSelected ? 'bg-purple-500/20 text-purple-400 border border-purple-500' : 'bg-white/5 hover:bg-white/10 text-white border border-transparent'}`} data-id="element-1671">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-lg font-medium 
                    ${isSelected ? 'bg-purple-500/20 text-purple-400 border border-purple-500' : 'bg-black/60 text-gray-400 border border-gray-600'}`} data-id="element-1672">
                    {option.id}
                  </div>
                  <span className="flex-1" data-id="element-1673">{option.text}</span>
                </button>;
          })}
          </div>
        </div>
        {/* Right sidebar - Control panel */}
        <div className="lg:w-1/3" data-id="element-1674">
          <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 sticky top-24" data-id="element-1675">
            <div className="relative p-4 bg-black/90 rounded-xl" data-id="element-1676">
              {/* Navigation buttons */}
              <div className="space-y-3 mb-6" data-id="element-1677">
                <button onClick={handlePreviousQuestion} disabled={currentQuestionIndex === 0} className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors 
                  ${currentQuestionIndex === 0 ? 'bg-gray-800/50 text-gray-500 cursor-not-allowed' : 'bg-white/5 hover:bg-white/10 text-white'}`} data-id="element-1678">
                  <ChevronLeft className="w-5 h-5" data-id="element-1679" />
                  Önceki Soru
                </button>
                <button onClick={handleClearAnswer} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors" data-id="element-1680">
                  <X className="w-5 h-5" data-id="element-1681" />
                  Soruyu Boş Bırak
                </button>
                <button onClick={handleNextQuestion} disabled={currentQuestionIndex === currentSubject.questions.length - 1} className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors 
                  ${currentQuestionIndex === currentSubject.questions.length - 1 ? 'bg-gray-800/50 text-gray-500 cursor-not-allowed' : 'bg-white/5 hover:bg-white/10 text-white'}`} data-id="element-1682">
                  Sonraki Soru
                  <ChevronRight className="w-5 h-5" data-id="element-1683" />
                </button>
                <button onClick={() => setShowFinishConfirm(true)} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-lg hover:opacity-90 transition-colors mt-4" data-id="element-1684">
                  Oturumu Bitir
                </button>
              </div>
              {/* Subject dropdown */}
              <div className="mb-4" data-id="element-1685">
                <div className="relative" data-id="element-1686">
                  <button onClick={() => setShowSubjectDropdown(!showSubjectDropdown)} className="w-full flex items-center justify-between gap-2 px-4 py-2 bg-black/80 rounded-lg border border-purple-500/30 hover:border-purple-500/50 transition-colors" data-id="element-1687">
                    <span className="text-white font-medium" data-id="element-1688">
                      {currentSubject.displayName}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-purple-400 transition-transform ${showSubjectDropdown ? 'rotate-180' : ''}`} data-id="element-1689" />
                  </button>
                  {showSubjectDropdown && <div className="absolute top-full left-0 mt-1 z-10 w-full bg-gray-900/95 border border-gray-700 rounded-lg shadow-lg py-1 max-h-60 overflow-y-auto" data-id="element-1690">
                      {examData.subjects.map(subject => <button key={subject.id} onClick={() => handleSubjectChange(subject.id)} className={`w-full text-left px-4 py-2 transition-colors ${selectedSubject === subject.id ? 'bg-purple-500/20 text-purple-400' : 'text-white hover:bg-purple-500/10'}`} data-id="element-1691">
                          {subject.displayName}
                        </button>)}
                    </div>}
                </div>
              </div>
              {/* Subject-specific optical form with each subject starting from 1 */}
              <div className="mt-4 border-t border-gray-800 pt-4" data-id="element-1692">
                <h3 className="text-sm font-medium mb-3 text-gray-400" data-id="element-1693">
                  {currentSubject.name} Optik Form
                </h3>
                <div className="max-h-[400px] overflow-y-auto pr-2 space-y-2" data-id="element-1694">
                  {currentSubjectQuestions.map((question, index) => {
                  const selectedOption = getSelectedOption(question.id);
                  return <div key={question.id} className="flex items-center py-2 border-b border-gray-800/30" data-id="element-1695">
                        <div className="w-10 font-medium text-center cursor-pointer" onClick={() => handleGoToQuestion(index)} data-id="element-1696">
                          {index + 1}
                        </div>
                        <div className="flex-1 flex items-center justify-between" data-id="element-1697">
                          {['A', 'B', 'C', 'D'].map(optionId => <button key={optionId} onClick={() => handleSelectAnswer(question.id, optionId)} className={`w-8 h-8 rounded-full flex items-center justify-center text-sm 
                                ${selectedOption === optionId ? 'bg-purple-500/20 border-2 border-purple-500 text-purple-400' : 'border border-gray-600 text-gray-400 hover:border-gray-400'}`} data-id="element-1698">
                              {optionId}
                            </button>)}
                        </div>
                      </div>;
                })}
                </div>
              </div>
              {/* Stats */}
              <div className="pt-4 border-t border-gray-800 mt-4" data-id="element-1699">
                <div className="space-y-2" data-id="element-1700">
                  <div className="flex justify-between items-center" data-id="element-1701">
                    <span className="text-gray-400" data-id="element-1702">
                      {currentSubject.name} Toplam
                    </span>
                    <span className="font-medium" data-id="element-1703">
                      {currentSubject.questions.length}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-1704">
                    <span className="text-gray-400" data-id="element-1705">Cevaplanan</span>
                    <span className="font-medium text-purple-400" data-id="element-1706">
                      {getAnsweredQuestionsCount()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-1707">
                    <span className="text-gray-400" data-id="element-1708">Boş</span>
                    <span className="font-medium" data-id="element-1709">
                      {currentSubject.questions.length - getAnsweredQuestionsCount()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Finish confirmation modal */}
      {showFinishConfirm && <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50" data-id="element-1710">
          <div className="border border-purple-500 rounded-xl overflow-hidden w-full max-w-md" data-id="element-1711">
            <div className="bg-black/90 rounded-xl p-6" data-id="element-1712">
              <div className="flex flex-col items-center space-y-4 text-center" data-id="element-1713">
                <AlertTriangle className="w-12 h-12 text-yellow-400" data-id="element-1714" />
                <h3 className="text-xl font-bold" data-id="element-1715">Oturumu Bitirmeyi Onayla</h3>
                <p className="text-gray-300" data-id="element-1716">
                  Bu oturumu bitirmek istediğinize emin misiniz?{' '}
                  <strong data-id="element-1717">{getUnansweredCount()} soruyu boş bıraktınız.</strong>{' '}
                  Onaylarsanız cevaplarınızı bir daha değiştiremezsiniz.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 w-full mt-4" data-id="element-1718">
                  <button onClick={() => setShowFinishConfirm(false)} className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg transition-colors" data-id="element-1719">
                    İptal
                  </button>
                  <button onClick={handleFinishSession} className="flex-1 px-4 py-2 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-lg hover:opacity-90 transition-colors" data-id="element-1720">
                    Evet, Oturumu Bitir
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}